import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Fade from '@material-ui/core/Fade';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  layerImage: {
    objectFit: 'cover',
    position: 'absolute',
    left: 0,
    height: '100%',
    width: '100%',
  },
}));

const NO_SRC = 'NO_SRC';

const LoadAndFadeImage = ({ src, active, onReady, crossfade, in: inProp, zIndex, ...props }) => {
  const [loaded, setLoaded] = useState('');
  useEffect(() => {
    src ? setLoaded('') : setLoaded(NO_SRC);
  }, [src]);

  useEffect(() => {
    (loaded === NO_SRC || loaded === src) && active && onReady && onReady({ src });
  }, [loaded, active, src, onReady]);

  const style = useMemo(() => ({ zIndex }), [zIndex]);

  const handleOnLoad = () => {
    setLoaded(src);
  };

  if (!src) {
    return null;
  }

  return (
    <Fade {...props} exit={crossfade} in={crossfade ? inProp : !!loaded} style={style}>
      <img src={src} alt="" onLoad={handleOnLoad} />
    </Fade>
  );
};

LoadAndFadeImage.propTypes = {
  src: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  onReady: PropTypes.func,
  crossfade: PropTypes.bool,
  in: PropTypes.bool,
  isNext: PropTypes.bool,
  zIndex: PropTypes.number,
};

LoadAndFadeImage.defaultProps = {
  crossfade: false,
  in: true,
  isNext: false,
};

export const LayerFadeImage = ({ src, crossfade, bufferSize }) => {
  const [state, setState] = useState({
    images: [],
    currentCount: 0,
    active: 0,
  });
  useEffect(() => {
    setState(({ images, currentCount, ...state }) => {
      const existingImage = images.find((image) => image.src === src);
      if (existingImage) {
        const { count } = existingImage;
        return {
          ...state,
          images: images.map((image) => ({ ...image, zIndex: count === image.count ? 0 : -1 })),
          active: count,
          currentCount,
        };
      }
      const count = images.length < bufferSize ? images.length : (currentCount + 1) % bufferSize;
      images[count] = { src, count };
      return {
        ...state,
        images: images.map((image) => ({ ...image, zIndex: count === image.count ? 0 : -1 })),
        currentCount: count,
      };
    });
  }, [src, bufferSize]);

  const classes = useStyles();

  const { currentCount, active, images } = state;

  const handleReady = useCallback(
    ({ src }) =>
      (!src || src === images[currentCount].src) &&
      setState((state) => {
        return {
          ...state,
          active: currentCount,
        };
      }),
    [currentCount]
  );

  return (
    <>
      {images.map(({ src, count, zIndex }) => (
        <LoadAndFadeImage
          key={count}
          timeout={1000}
          src={src}
          zIndex={zIndex}
          className={classes.layerImage}
          active={currentCount === count}
          in={active === count}
          onReady={handleReady}
          crossfade={crossfade}
        />
      ))}
    </>
  );
};

LayerFadeImage.propTypes = {
  src: PropTypes.string.isRequired,
  crossfade: PropTypes.bool,
  bufferSize: PropTypes.number,
};

LayerFadeImage.defaultProps = {
  crossfade: false,
  bufferSize: 20,
};

export default LayerFadeImage;
